/**
 * Phone sign-in for my.x9.app (existing X9 accounts).
 * Uses signInWithPhoneNumber — not anonymous link (portal is for real accounts).
 */
import {
  RecaptchaVerifier,
  signInWithPhoneNumber,
  signOut,
  type ConfirmationResult,
  type User,
} from 'firebase/auth';
import { getFirebaseAuth } from '../config/firebase';
import { invalidateBillingSummaryCache } from '../hooks/useBillingSummary';
import { resetUserDocStore } from '../hooks/userDocStore';

let recaptchaVerifier: RecaptchaVerifier | null = null;

function ensureRecaptcha(containerId: string): RecaptchaVerifier {
  if (recaptchaVerifier) return recaptchaVerifier;
  recaptchaVerifier = new RecaptchaVerifier(getFirebaseAuth(), containerId, {
    size: 'invisible',
  });
  return recaptchaVerifier;
}

export function clearRecaptchaVerifier(): void {
  if (!recaptchaVerifier) return;
  try {
    recaptchaVerifier.clear();
  } catch {
    // ignore
  }
  recaptchaVerifier = null;
}

export async function sendPhoneCode(
  e164Phone: string,
  containerId = 'recaptcha-container',
): Promise<ConfirmationResult> {
  const auth = getFirebaseAuth();
  try {
    const verifier = ensureRecaptcha(containerId);
    return await signInWithPhoneNumber(auth, e164Phone, verifier);
  } catch (err) {
    clearRecaptchaVerifier();
    throw err;
  }
}

export function describeSendError(err: unknown): string {
  const code = typeof err === 'object' && err && 'code' in err
    ? String((err as { code?: string }).code)
    : '';
  switch (code) {
    case 'auth/invalid-phone-number':
      return 'Esse telefone não parece válido. Confira o DDD e o país.';
    case 'auth/too-many-requests':
      return 'Muitas tentativas. Espere um minuto e tente de novo.';
    case 'auth/quota-exceeded':
      return 'A cota de SMS está cheia agora. Tente mais tarde.';
    case 'auth/captcha-check-failed':
      return 'A verificação de segurança falhou. Recarregue a página.';
    case 'auth/network-request-failed':
      return 'Falha de rede. Confira a conexão e tente de novo.';
    default:
      return err instanceof Error ? err.message : 'Falha ao enviar SMS';
  }
}

export async function confirmPhoneCode(
  confirmation: ConfirmationResult,
  code: string,
): Promise<User> {
  const cred = await confirmation.confirm(code.trim());
  clearRecaptchaVerifier();
  return cred.user;
}

export async function signOutBilling(): Promise<void> {
  clearRecaptchaVerifier();
  // The gate's cached answer belongs to the account that is leaving.
  resetUserDocStore();
  invalidateBillingSummaryCache();
  await signOut(getFirebaseAuth());
}

/**
 * Drop a brand-new Auth session when the user declines Terms before a
 * profile is written. Falls back to sign-out if delete is rejected.
 */
export async function discardUnregisteredAuthUser(): Promise<void> {
  const auth = getFirebaseAuth();
  const user = auth.currentUser;
  clearRecaptchaVerifier();
  if (!user) return;
  try {
    await user.delete();
  } catch {
    await signOut(auth);
  }
}
